import { isEmpty, isString } from 'lodash';
import { stringifySearchWith } from './tanstack-copy';
import { parseKibanaToOsdPath } from 'states/osd/osd.route.helpers';

function stripNullish<T extends Record<string, unknown>>(search: T): T {
  return Object.fromEntries(
    Object.entries(search)
      .filter(([, value]) => !isEmpty(value))
      .map(([key, val]) => [key, isString(val) && !isNaN(Number(val)) ? Number(val) : val]),
  ) as T;
}

export function tanstackUrlSearch(params: Record<string, unknown>) {
  return stringifySearchWith(JSON.stringify, JSON.parse)(stripNullish(params));
}

export function uriSafe(str: string | number): string {
  return encodeURIComponent(str);
}

function isJSONStringifyObject(str: string) {
  try {
    const parsed = JSON.parse(str);

    return typeof parsed === 'object';
  } catch (e) {
    return false;
  }
}

export function parseStringifyValues(params: Record<string, unknown>) {
  return Object.fromEntries(
    Object.entries(params).map(([key, val]) => [
      key,
      val && isString(val) && isJSONStringifyObject(val) ? JSON.parse(val) : val,
    ]),
  );
}

/**
 * The only legacy OSD params the new app reads. Kibana-internal params (`embed`, `kibanaRoute`,
 * `forceShowQueryBar`, …) must never be forwarded — app-ui sets those itself and forwarding them
 * either no-ops or breaks boot.
 */
const OSD_PASSTHROUGH_KEYS = ['_a', '_g', 'id', 'switchToAccountId'] as const;

type OsdPassthroughKey = typeof OSD_PASSTHROUGH_KEYS[number];

export const pickOsdParams = (params: Record<string, unknown>): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(params).filter(
      ([key, value]) => OSD_PASSTHROUGH_KEYS.includes(key as OsdPassthroughKey) && value !== null && value !== undefined,
    ),
  );

/**
 * Turns a legacy OSD `resource` path into the new app's `app` segment plus iframe `hash`.
 * The hash must keep a leading slash (`/view/{id}`, `/list`) or OSD's hash router cannot match it.
 */
export const splitOsdResource = (resource: unknown): { app: string; hash?: string } => {
  const [app, ...hashParts] = parseKibanaToOsdPath(typeof resource === 'string' ? resource : '').split('/');
  const hash = hashParts.filter(Boolean).join('/');

  return { app, hash: hash ? `/${hash}` : undefined };
};
